/* eslint-disable max-len */
import React, { useRef, useState } from 'react';

import { get } from 'lodash';
import { useNavigate } from 'react-router-dom';

import { uploadMusicTypes } from '@/types';
import { routesConstants } from '@/constants';
// import { uploadMusicHelpers } from '@/helpers';
import { Button } from '@/kaya';
import { songsStore, uploadMusicStore } from '@/store';
// import { songActions } from '@/actions';

import styles from './TermsAndCondition.module.scss';

const TermsAndCondition = () => {
  const { setFormValues, formvalues, setIsUploadDone } = uploadMusicStore();
  const { addSong } = songsStore();

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const hadnleImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    setFormValues({ ...formvalues, signature: file, signatureUrl: URL.createObjectURL(file) });
  };

  const handleUploadSong = async () => {
    // Uploads now handled earlier in the flow; this step is for terms acceptance and signature only
    setIsLoading(true);

    // Assume song is already created; just finalize with signature if needed
    // For now, mark as done and navigate
    setIsLoading(false);
    navigate(routesConstants.ROUTES.UPLOAD_MUSIC);
    setIsUploadDone(true);
    // addSong(get(data, 'data', '')); // Comment out if data not available
    setFormValues(uploadMusicTypes.INITIAL_STATE);
  };

  return (
    <div className={styles.TermsAndCondition}>
      <p className={styles.tnc}>Terms and Condition</p>
      <p className={styles.lastUpdated}>Last Updated: January 16, 2023</p>
      <div className={styles.description}>
        <section>
          <h2>1. Introduction</h2>
          <p>
            {`These Terms and Conditions ("Terms") govern your relationship as an artist with 4Play,
            the platform offering background music for businesses. By registering as an artist and
            submitting your music to the 4Play repertoire, you agree to comply with these Terms.
            Please read them carefully, as they outline your rights and obligations, as well as
            4Play's role in managing and distributing your music. These Terms apply in addition to
            any specific agreements you may have with 4Play.`}
          </p>
          <p>
            By accepting these Terms, you confirm that you are either the legal owner of the music
            or have the necessary rights and permissions to distribute and monetize the music
            through the 4Play platform.
          </p>
        </section>

        <section>
          <h2>2. Artist Registration</h2>
          <p>
            {`To upload music to 4Play's repertoire, you must complete the artist registration
            process. This includes providing accurate personal information such as your name,
            contact details, payment information, and tax identification number, if applicable. By
            registering, you affirm that the information you provide is accurate and up-to-date, and
            you are responsible for keeping this information current throughout your relationship
            with 4Play.`}
          </p>
          <h3>Account Security</h3>
          <p>
            You are responsible for maintaining the confidentiality of your account login
            information. Any activity conducted through your account will be your responsibility.
            Notify 4Play immediately if you suspect any unauthorized use of your account.
          </p>
          <h3>Eligibility</h3>
          <p>
            You must be at least 18 years of age or the legal age of majority in your country of
            residence to register as an artist. By agreeing to these Terms, you confirm that you
            meet these requirements.
          </p>
        </section>

        <section>
          <h2>3. Grant of License</h2>
          <p>
            By uploading your music to 4Play, you grant 4Play a non-exclusive, worldwide,
            royalty-free license to use, reproduce, distribute, and publicly perform your music
            through the 4Play platform. This license includes:
          </p>
          <ul>
            <li>
              <strong>Distribution:</strong> 4Play may stream your music to businesses that
              subscribe to the service, allowing them to play it as background music in their
              establishments.
            </li>
            <li>
              <strong>Public Performance:</strong> The license permits business subscribers to
              publicly perform your music in their premises, in accordance with 4Play&apos;s
              subscription agreements with these businesses.
            </li>
            <li>
              <strong>Reproduction:</strong> 4Play may create and store copies of your music for
              internal purposes, including playlist management, archiving, backup, and technical
              optimization.
            </li>
            <li>
              <strong>Promotion:</strong> You grant 4Play permission to use your name, likeness, and
              the music you upload for promotional purposes on its platform, website, and marketing
              materials.
            </li>
          </ul>
          <p>
            The license remains in effect for as long as your music is available on the platform. If
            you request the removal of your music, 4Play will process the removal within a
            reasonable timeframe, but this does not impact any licenses granted for prior use.
          </p>
        </section>

        <section>
          <h2>4. Ownership and Rights</h2>
          <p>
            You retain full ownership of all music and content you upload to 4Play. By submitting
            your music, you affirm that you are the sole owner of all rights to the music, including
            any composition, recording, and performance rights, or that you have obtained all
            necessary licenses and permissions from any third-party rights holders.
          </p>
          <h3>Third-Party Claims</h3>
          <p>
            In the event that a third party makes a claim related to copyright infringement,
            compensation, or royalties, you will bear full responsibility for any direct and/or
            indirect costs associated with resolving the claim, including legal fees, settlements,
            or royalties owed. 4Play will not be held liable for any claims arising from your
            failure to secure proper rights or permissions before submitting music to the platform.
          </p>
          <h3>Indemnity</h3>
          <p>
            You agree to indemnify and hold 4Play harmless from any claims, disputes, or damages
            arising from your music, including but not limited to copyright infringement or
            unauthorized use of third-party materials.
          </p>
        </section>

        <section>
          <h2>5. Royalties and Payment</h2>
          <h3>Revenue Share</h3>
          <p>
            Artists will receive a revenue share based on the number of plays their music receives.
            The rate will range between $0.001 to $0.005 per play, depending on the subscription
            package chosen by the business playing the music. Please note that during the 10-day
            trial period offered to new clients, artists will not receive any payment for plays of
            their music.
          </p>
          <h3>Payment Schedule</h3>
          <p>
            {`Payment is made to the artist upon request. It is the artist's responsibility to log
            into their personal area on the 4Play platform and request withdrawal of the funds to
            their bank account. The minimum withdrawal amount is $100. Payments will be processed
            with the applicable tax deductions in accordance with the artist's country of residence
            and tax policy. Failure to provide accurate tax information may result in withholding of
            payments.`}
          </p>
          <h3>Taxation</h3>
          <p>
            You are responsible for complying with tax regulations in your country of residence.
            4Play will deduct any necessary taxes before issuing payments, in accordance with
            applicable laws.
          </p>
          <h3>Changes to Payment Terms</h3>
          <p>
            4Play reserves the right to update its payment policies from time to time. Any changes
            will be communicated in advance via email or through notifications on the platform.
          </p>
        </section>

        <section>
          <h2>6. Usage Reports</h2>
          <p>
            {`4Play provides detailed reports that track where, when, and how often your music has
            been played. These reports are accessible through your artist account and are designed
            to help you understand your music's performance across the platform.`}
          </p>
          <h3>Metrics</h3>
          <p>
            Reports will include data such as the number of plays, locations of businesses where
            your music was played, and the engagement level of users. Artists are prohibited from
            attempting to artificially inflate play counts, likes, or other metrics through
            fraudulent activity or third-party manipulation.
          </p>
        </section>

        <section>
          <h2>7. Removal of Content</h2>
          <p>
            4Play reserves the right to remove any content from its platform at its discretion.
            Reasons for removal may include, but are not limited to:
          </p>
          <ul>
            <li>
              <strong>Copyright Infringement:</strong> If your music is found to infringe on the
              intellectual property rights of others, it will be removed from the platform.
            </li>
            <li>
              <strong>Quality Control:</strong> Music that does not meet 4Play&apos;s quality
              standards may be removed.
            </li>
            <li>
              <strong>Violation of Terms:</strong> If you violate these Terms, engage in fraudulent
              behavior, or are found to be in breach of any agreements with 4Play, your music may be
              removed.
            </li>
          </ul>
          <p>
            You may also request the removal of your music at any time by contacting 4Play. Upon
            receiving such a request, 4Play will take reasonable steps to remove your content within
            a 30-day period. Any licenses granted for prior use of the music will remain valid.
          </p>
        </section>

        <section>
          <h2>8. Artist Responsibilities</h2>
          <p>As an artist on 4Play, you agree to:</p>
          <ul>
            <li>
              <strong>Comply with Legal Requirements:</strong> Ensure that all music you upload
              complies with applicable copyright and intellectual property laws.
            </li>
            <li>
              <strong>Provide Accurate Information:</strong> Keep your account information,
              including payment and tax details, up to date.
            </li>
            <li>
              <strong>Maintain High Standards:</strong> Ensure that your music meets 4Play&apos;s
              quality requirements, both in terms of composition and audio production. Poorly
              produced or low-quality submissions may be removed.
            </li>
          </ul>
        </section>

        <section>
          <h2>9. Prohibited Activities</h2>
          <p>The following activities are prohibited on the 4Play platform:</p>
          <ul>
            <li>
              <strong>Infringement:</strong> Uploading music that you do not have the rights to, or
              music that infringes on the rights of others, is strictly prohibited.
            </li>
            <li>
              <strong>Fraudulent Activity:</strong> Engaging in fraudulent behavior, including
              artificially inflating play counts or engaging in unauthorized manipulation of
              platform metrics, is grounds for immediate account termination.
            </li>
            <li>
              <strong>Unauthorized Sharing:</strong> You may not share your artist account or allow
              others to use your login credentials without permission from 4Play.
            </li>
          </ul>
        </section>

        <section>
          <h2>10. Termination of Agreement</h2>
          <p>
            4Play reserves the right to terminate your account or your access to the platform at any
            time, for any reason, including but not limited to violation of these Terms, breach of
            your agreements with 4Play, or engagement in illegal or fraudulent activities. In the
            event of termination, your music will be removed from the platform within 30 days.
          </p>
          <p>
            You may also terminate your participation by providing written notice. Upon termination,
            all licenses granted to 4Play will be revoked, except for any licenses granted for the
            use of your music prior to termination.
          </p>
        </section>

        <section>
          <h2>11. Liability and Indemnification</h2>
          <p>
            You agree to indemnify and hold 4Play harmless from any claims, damages, or legal
            actions arising from your use of the platform or the music you upload. This includes,
            but is not limited to, any claims of copyright infringement, intellectual property
            disputes, or violation of third-party rights. In the event of any claims, you will be
            responsible for covering all direct and/or indirect costs, including legal fees,
            settlements, or compensation.
          </p>
          <p>
            4Play is not liable for any indirect, incidental, or consequential damages, including
            loss of revenue or reputation, arising from the use of the platform.
          </p>
        </section>

        <section>
          <h2>12. Governing Law</h2>
          <p>
            This agreement is governed by the laws of the State of Israel. Any disputes arising from
            these Terms will be subject to the exclusive jurisdiction of the Israeli courts.
          </p>
        </section>

        <section>
          <h2>13. Changes to the Terms</h2>
          <p>
            4Play reserves the right to modify these Terms at any time. If material changes are
            made, 4Play will notify artists via email or through the platform. Your continued use of
            the platform following the effective date of any changes constitutes your acceptance of
            the updated Terms.
          </p>
        </section>

        <section>
          <h2>14. Contact Information</h2>
          <p>
            If you have any questions or concerns regarding these Terms and Conditions, or your
            relationship with 4Play, please contact us at{' '}
            <a href="mailto:info@4-play.io">info@4-play.io</a>.
          </p>
        </section>
      </div>

      {!formvalues.signatureUrl ? (
        <Button
          width="200px"
          onClick={() => {
            inputRef.current?.click();
          }}
        >
          Upload Image
        </Button>
      ) : (
        <img src={formvalues.signatureUrl} alt="signature" className={styles.thumbnail} />
      )}

      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        ref={inputRef}
        onChange={hadnleImage}
      />
      <div style={{ height: 40 }}></div>
      <Button
        type="Primary"
        width="200px"
        onClick={handleUploadSong}
        disabled={!formvalues.signature}
        isLoading={isLoading}
      >
        Accept
      </Button>
    </div>
  );
};

export default TermsAndCondition;
